import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControlLabel, MenuItem, Skeleton, Switch, TextField, Tooltip, Typography
} from '@mui/material';
import { notify } from '../../../notifications';
import { apiError } from '../../Documents/format';
import { MONTH_NAMES } from '../constants';
import { useTimesheetRun } from '../hooks/useTimesheetRun';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { CycleSettingsInput, CycleStatus } from '../types';
import { addDays, extractFolderId, fmtDay, fmtMonthDay, isFolderId, planText } from '../utils';
import ConfirmDialog from './ConfirmDialog';
import ReasonDialog from './ReasonDialog';

interface Props {
  runCtl: ReturnType<typeof useTimesheetRun>;
  /** Меняется, когда запуск завершился: сводка по папкам перечитывается */
  refreshKey: string;
  /** Операция выполнена и записана в журнал; switch - год переключён: страница обновляет сводку, список и настройки */
  onDone: (kind: 'settings' | 'prepare' | 'switch') => void;
}

interface Form {
  auto: boolean;
  parentFolder: string;
  prepareFrom: string;
  prevDays: number;
}

type Action = 'prepare' | 'switch' | 'settings' | null;

const BORDER = '#e2e8f0';
const sectionLabel = { mb: 1, fontSize: '0.8rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 } as const;
const buttonSx = { textTransform: 'none', fontWeight: 600, borderRadius: 1.5 } as const;
/** Число дней в месяце для выбора даты подготовки: февраль с 29, чтобы дата «29 февраля» оставалась доступной */
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const pad = (n: number): string => String(n).padStart(2, '0');

/** Состояние трёх папок года одной строкой */
function FolderLine({ title, text, muted }: { title: string; text: string; muted?: boolean }) {
  return (
    <Box sx={{ display: 'flex', gap: 1.5, py: 0.6, borderBottom: '1px solid #f1f5f9', '&:last-of-type': { borderBottom: 'none' } }}>
      <Typography variant="body2" sx={{ width: 150, flexShrink: 0, fontWeight: 600, color: '#334155' }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: muted ? '#94a3b8' : '#475569' }}>{text}</Typography>
    </Box>
  );
}

/**
 * Автоматизация по годам: состояние папок (текущая, следующая, прошлая), кнопки «Подготовить» и «Переключить»
 * и расписание. «Подготовить» запрашивает только подтверждение, настройки и переключение - причину; всё попадает в журнал операций.
 */
const CyclePanel: React.FC<Props> = ({ runCtl, refreshKey, onDone }) => {
  const [status, setStatus] = useState<CycleStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [edits, setEdits] = useState<Partial<Form>>({});
  const [action, setAction] = useState<Action>(null);

  const { running } = runCtl;

  useEffect(() => {
    const controller = new AbortController();
    timesheetApi.cycle(controller.signal)
      .then(data => { setStatus(data); setLoadError(null); })
      .catch(err => { if (!isCancelled(err)) setLoadError(apiError(err, 'Не удалось загрузить состояние автоматизации')); });
    return () => controller.abort();
  }, [refreshKey, reloadToken]);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  const form: Form | null = useMemo(() => {
    if (!status) return null;
    return {
      auto: status.auto, parentFolder: status.parentFolder, prepareFrom: status.prepareFrom, prevDays: status.prevDays, ...edits
    };
  }, [status, edits]);

  const patch = useCallback((values: Partial<Form>) => setEdits(prev => ({ ...prev, ...values })), []);

  const parentId = form ? extractFolderId(form.parentFolder) : '';
  const parentValid = !parentId || isFolderId(parentId);

  /** Только действительно изменённые поля: сервер пишет в журнал, что было и что стало */
  const changes = useMemo(() => {
    const result: CycleSettingsInput = {};
    if (!status || !form) return result;
    if (form.auto !== status.auto) result.auto = form.auto;
    if (parentId !== status.parentFolder) result.parentFolder = parentId;
    if (form.prepareFrom !== status.prepareFrom) result.prepareFrom = form.prepareFrom;
    if (form.prevDays !== status.prevDays) result.prevDays = form.prevDays;
    return result;
  }, [status, form, parentId]);

  const dirty = Object.keys(changes).length > 0;
  const prevDaysValid = form !== null && status !== null && Number.isInteger(form.prevDays) && form.prevDays >= 1 && form.prevDays <= status.maxPrevDays;

  const [month, day] = form ? form.prepareFrom.split('-').map(Number) : [12, 1];
  const setMonthDay = (m: number, d: number) => patch({ prepareFrom: `${pad(m)}-${pad(Math.min(d, DAYS_IN_MONTH[m - 1]))}` });

  const submitSettings = useCallback(async (reason: string) => {
    const res = await timesheetApi.saveCycleSettings(changes, reason);
    setEdits({});
    notify.success(res.changed ? 'Настройки автоматизации сохранены' : 'Настройки не изменились');
    reload();
    onDone('settings');
  }, [changes, reload, onDone]);

  const submitPrepare = useCallback(async () => {
    const res = await timesheetApi.prepareYear();
    runCtl.refresh();
    notify.success(
      `${res.folderCreated ? 'Папка создана' : 'Папка найдена'}, создание табелей ${res.year} года запущено`
    );
    reload();
    onDone('prepare');
  }, [runCtl, reload, onDone]);

  const submitSwitch = useCallback(async (reason: string) => {
    const res = await timesheetApi.switchYear(reason);
    notify.success(`Теперь рабочий год - ${res.to.year}. Табели ${res.from.year} года доступны до ${fmtDay(res.prevUntil)}`);
    reload();
    onDone('switch');
  }, [reload, onDone]);

  const closeDialog = useCallback(() => setAction(null), []);

  if (loadError && !status) {
    return <Alert severity="error" action={<Button color="inherit" size="small" onClick={reload}>Повторить</Button>}>{loadError}</Alert>;
  }
  if (!status || !form) return <Skeleton variant="rounded" height={260} />;

  const { current, next, prev } = status;
  const ready = Boolean(next && next.ready);
  const nextYear = next ? next.year : (current ? current.year + 1 : null);
  const noFolder = !current;

  const prepareDisabled = noFolder || running || (ready && next !== null && next.missing === 0);
  const prepareHint = noFolder
    ? 'Сначала укажите папку текущего года'
    : running ? 'Создание табелей уже идёт'
      : ready && next !== null && next.missing === 0 ? `Во всех аптеках уже есть табель ${next.year} года` : '';

  const switchHint = !ready ? 'Сначала подготовьте следующий год' : running ? 'Дождитесь окончания создания табелей' : '';
  const prevUntilPreview = fmtDay(addDays(status.today, form.prevDays - 1));

  // что именно сделает «Подготовить»: показывается в окне подтверждения вместо ввода причины
  const prepareTitle = ready && next ? `Досоздать табели ${next.year} года` : `Подготовить ${nextYear || ''} год`;
  const prepareItems: React.ReactNode[] = ready && next
    ? [
      <>Табели {next.year} года будут созданы аптекам, у которых их ещё нет: {next.missing} из {next.total}.</>,
      'Уже созданные табели не затрагиваются.'
    ]
    : [
      <>В Google Drive будет найдена или создана папка «Табели {nextYear}» {status.parentFolder ? 'в родительской папке из настроек' : 'рядом с папкой текущего года'}.</>,
      <>В ней будут созданы табели для аптек сети{current ? ` (${current.total})` : ''}; уже существующие не затрагиваются.</>
    ];
  prepareItems.push(
    'Создание идёт в фоне: ход и результат по каждой аптеке видны во вкладке «Журнал операций», запуск можно остановить.',
    current
      ? `Текущий ${current.year} год не меняется: аптека работает как раньше, год переключается отдельно.`
      : 'Текущий год не меняется.',
    'В журнале сохранится, кто и когда запустил подготовку.'
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box>
        <Typography sx={sectionLabel}>Состояние по годам</Typography>
        <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5, py: 0.5 }}>
          <FolderLine
            title={current ? `Текущий, ${current.year}` : 'Текущий'}
            text={current ? `табелей ${current.created} из ${current.total}` : 'папка не указана'}
            muted={!current}
          />
          <FolderLine
            title={nextYear ? `Следующий, ${nextYear}` : 'Следующий'}
            text={ready && next ? `папка подготовлена, табелей ${next.created} из ${next.total}` : 'не подготовлен'}
            muted={!ready}
          />
          <FolderLine
            title={prev ? `Прошлый, ${prev.year}` : 'Прошлый'}
            text={prev ? (prev.active ? `доступен до ${fmtDay(prev.until)}` : `срок доступа истёк ${fmtDay(prev.until)}`) : 'нет'}
            muted={!prev || !prev.active}
          />
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>Сегодня {fmtDay(status.today)}. Ближайшее действие: {planText(status.plan)}.</Typography>
      </Box>

      <Box>
        <Typography sx={sectionLabel}>Действия</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mb: 1.5 }}>
          Подготовка создаёт папку нового года рядом с текущей и табели всей сети. Текущий год при этом не меняется:
          аптека работает как раньше, пока год не переключён.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title={prepareHint}>
            <span>
              <Button
                variant="contained" disabled={prepareDisabled} onClick={() => setAction('prepare')} sx={buttonSx}
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {ready && next ? `Досоздать табели ${next.year}` : `Подготовить ${nextYear || ''} год`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={switchHint}>
            <span>
              <Button
                variant="outlined" color="warning" disabled={!ready || running} onClick={() => setAction('switch')} sx={buttonSx}
              >
                {ready && next ? `Переключить на ${next.year} год` : 'Переключить год'}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box>
        <Typography sx={sectionLabel}>Расписание</Typography>
        <FormControlLabel
          control={<Switch checked={form.auto} onChange={e => patch({ auto: e.target.checked })} />}
          label="Автоматизация по расписанию"
        />
        <Typography variant="body2" sx={{ color: '#64748b', mb: 2, ml: 0.5 }}>
          {form.auto
            ? `С ${fmtMonthDay(form.prepareFrom)} система сама создаст папку и табели следующего года, а 1 января переключит год. `
            + `Прошлый год останется доступным ${form.prevDays} дн. (до ${prevUntilPreview} при переключении сегодня).`
            : 'Выключена: подготовку и переключение года выполняют операторы кнопками выше.'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <TextField
            size="small" label="Родительская папка для новых лет" value={form.parentFolder} sx={{ flex: '1 1 340px' }}
            onChange={e => patch({ parentFolder: e.target.value })}
            error={!parentValid}
            helperText={!parentValid ? 'Укажите ссылку на папку или её идентификатор' : 'Пусто - рядом с папкой текущего года'}
            inputProps={{ maxLength: 300 }}
          />
          <TextField
            select size="small" label="Подготовка с (месяц)" value={month} sx={{ width: 170 }}
            onChange={e => setMonthDay(Number(e.target.value), day)}
          >
            {MONTH_NAMES.map((name, i) => <MenuItem key={name} value={i + 1}>{name}</MenuItem>)}
          </TextField>
          <TextField
            select size="small" label="День" value={day} sx={{ width: 90 }}
            onChange={e => setMonthDay(month, Number(e.target.value))}
          >
            {Array.from({ length: DAYS_IN_MONTH[month - 1] }, (_, i) => i + 1).map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          <TextField
            size="small" type="number" label="Доступ к прошлому году, дней" value={form.prevDays} sx={{ width: 240 }}
            onChange={e => patch({ prevDays: Number(e.target.value) })}
            error={!prevDaysValid}
            helperText={prevDaysValid ? `От 1 до ${status.maxPrevDays}` : `Укажите число от 1 до ${status.maxPrevDays}`}
            inputProps={{ min: 1, max: status.maxPrevDays, step: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button
            variant="contained" disabled={!dirty || !parentValid || !prevDaysValid} onClick={() => setAction('settings')} sx={buttonSx}
          >
            Сохранить
          </Button>
          {dirty && <Button onClick={() => setEdits({})} sx={buttonSx}>Отменить изменения</Button>}
        </Box>
      </Box>

      <ReasonDialog
        open={action === 'settings'} title="Сохранить настройки автоматизации" confirmLabel="Сохранить"
        description="Изменения расписания вступают в силу сразу. Что было и что стало, попадёт в журнал операций."
        onClose={closeDialog} onSubmit={submitSettings}
      />
      <ConfirmDialog
        open={action === 'prepare'} title={prepareTitle} items={prepareItems}
        confirmLabel={ready ? 'Досоздать' : 'Подготовить'}
        onClose={closeDialog} onSubmit={submitPrepare}
      />
      <ReasonDialog
        open={action === 'switch'} title={`Переключить на ${next ? next.year : ''} год`} confirmLabel="Переключить" confirmColor="warning"
        description={next && current ? (
          <>
            Рабочим станет {next.year} год, а {current.year} год останется доступным {status.prevDays} дн. - до {fmtDay(addDays(status.today, status.prevDays - 1))}.
            {next.missing > 0 && <> У {next.missing} аптек табеля {next.year} года пока нет.</>}
            {' '}Операцию нельзя отменить кнопкой.
          </>
        ) : undefined}
        onClose={closeDialog} onSubmit={submitSwitch}
      />
    </Box>
  );
};

export default CyclePanel;
